import React, { useState } from 'react';
import { CreateNoteParams } from 'hooks/useNoteHooks';
import curry from 'lodash/curry';
import { OrderType } from 'types/OrderType';
import { PqlPrice } from 'types/PqlPrice';
import { UFOPart } from 'types/UFOTypes';
import { managePqlPriceUpdate } from './utils';
import { retrievePqlPrice } from './utils/retrievePqlPrice';

export interface IOrderDetailContext {
  order?: OrderType;
  setOrder: React.Dispatch<React.SetStateAction<OrderType | undefined>>;
  error?: string;
  setError: (value: string) => void;
  loading: boolean;
  setLoading: (value: boolean) => void;
  actionButtonsLoading: boolean;
  setActionButtonLoading: (value: boolean) => void;
  orderLocked: boolean;
  setLocked: (value: boolean) => void;
  editors: number;
  setEditors: (value: number) => void;
  authorized: boolean;
  setAuthorized: (r: boolean) => void;
  isEditing: boolean;
  setEdit: (e: boolean) => void;
  dialog: 'edition_time_warning' | 'edition_closed_warning' | 'none';
  setDialog: (d: 'edition_time_warning' | 'edition_closed_warning' | 'none') => void;
  orderIsEdited: boolean;
  setOrderIsEdited: (v: boolean) => void;
  partsAreEdited: boolean;
  setPartsAreEdited: (v: boolean) => void;
  vehicleIsEdited: boolean;
  setVehicleIsEdited: (v: boolean) => void;
  hideGlobalActions: boolean;
  setHideGlobalActions: (v: boolean) => void;
  requireApproval: boolean;
  setRequireApproval: (v: boolean) => void;
  requirePaymentInfo: boolean;
  setRequirePaymentInfo: (v: boolean) => void;
  pqlProvided: boolean;
  setPqlProvided: (e: boolean) => void;
  pqlPending: boolean;
  setPqlPending: (e: boolean) => void;
  note?: CreateNoteParams;
  setNote: (n: CreateNoteParams | undefined) => void;
  totalPrice?: number;
  setTotalPrice: (v: number) => void;
  someEmptyPrice: boolean;
  setSomeEmptyPrice: (v: boolean) => void;
  mountingOptionsModalOpen: UFOPart | null;
  setMountingOptionsModalOpen: React.Dispatch<React.SetStateAction<UFOPart | null>>;
  setPql: (mspn: string, price: number, tireMspn: string) => void;
  getPql: (mspn: string, tireMspn: string) => number | null;
  hideAppointment: boolean;
  setHideAppointment: (v: boolean) => void;
  hideStockSources: boolean;
  setHideStockSources: (v: boolean) => void;
  emptyPqlPrices: { category: string }[];
  setEmptyPqlPrices: (v: { category: string }[]) => void;
  fittingTires: string[];
  setFittingTires: React.Dispatch<React.SetStateAction<string[]>>;
}

export const OrderDetailContext = React.createContext<IOrderDetailContext>({} as IOrderDetailContext);

export function useOrderDetailContext(): IOrderDetailContext {
  const [authorized, setAuthorized] = useState<boolean>(true);
  const [order, setOrder] = useState<OrderType>();
  const [error, setError] = useState<string>();
  const [loading, setLoading] = useState<boolean>(false);
  const [actionButtonsLoading, setActionButtonLoading] = useState<boolean>(false);
  const [orderLocked, setLocked] = useState(false);
  const [isEditing, setEdit] = useState(false);
  const [pqlProvided, setPqlProvided] = useState(false);
  const [pqlPending, setPqlPending] = useState(false);
  const [editors, setEditors] = useState(0);
  const [dialog, setDialog] = useState<IOrderDetailContext['dialog']>('none');
  const [orderIsEdited, setOrderIsEdited] = useState<boolean>(false);
  const [partsAreEdited, setPartsAreEdited] = useState<boolean>(false);
  const [vehicleIsEdited, setVehicleIsEdited] = useState<boolean>(false);
  const [hideGlobalActions, setHideGlobalActions] = useState<boolean>(false);
  const [requireApproval, setRequireApproval] = useState<boolean>(false);
  const [requirePaymentInfo, setRequirePaymentInfo] = useState<boolean>(false);
  const [note, setNote] = useState<CreateNoteParams>();
  const [totalPrice, setTotalPrice] = useState<number>();
  const [someEmptyPrice, setSomeEmptyPrice] = useState(false);
  const [mountingOptionsModalOpen, setMountingOptionsModalOpen] = useState<UFOPart | null>(null);
  const [pqlPrices, setPqlPrices] = useState<PqlPrice[]>([]);
  const [hideAppointment, setHideAppointment] = useState<boolean>(false);
  const [hideStockSources, setHideStockSources] = useState<boolean>(false);
  const [emptyPqlPrices, setEmptyPqlPrices] = useState<{ category: string }[]>([]);
  const [fittingTires, setFittingTires] = useState<string[]>([]);

  const setPql = React.useMemo(() => curry(managePqlPriceUpdate)(setPqlPrices), [setPqlPrices]);
  const getPql = React.useMemo(() => curry(retrievePqlPrice)(pqlPrices), [pqlPrices]);

  return {
    actionButtonsLoading,
    setActionButtonLoading,
    dialog,
    setDialog,
    isEditing,
    setEdit,
    authorized,
    setAuthorized,
    error,
    setError,
    loading,
    setLoading,
    orderLocked,
    setLocked,
    editors,
    setEditors,
    orderIsEdited,
    setOrderIsEdited,
    hideGlobalActions,
    setHideGlobalActions,
    vehicleIsEdited,
    setVehicleIsEdited,
    requireApproval,
    setRequireApproval,
    requirePaymentInfo,
    setRequirePaymentInfo,
    pqlProvided,
    setPqlProvided,
    pqlPending,
    setPqlPending,
    note,
    setNote,
    partsAreEdited,
    setPartsAreEdited,
    totalPrice,
    setTotalPrice,
    someEmptyPrice,
    setSomeEmptyPrice,
    mountingOptionsModalOpen,
    setMountingOptionsModalOpen,
    order,
    setOrder,
    setPql,
    getPql,
    hideAppointment,
    setHideAppointment,
    hideStockSources,
    setHideStockSources,
    emptyPqlPrices,
    setEmptyPqlPrices,
    fittingTires,
    setFittingTires,
  };
}
